import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

function formatViews(views: number): string {
  if (views >= 10000) {
    return (views / 10000).toFixed(1) + '万';
  }
  return views.toString();
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该文章可能已被删除或链接失效</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const moreArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug !== article.categorySlug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: '每日吃瓜',
      url: 'https://hbfrsc.cn/',
    },
    publisher: {
      '@type': 'Organization',
      name: '每日吃瓜',
      url: 'https://hbfrsc.cn/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hbfrsc.cn/article/${article.slug}`,
    },
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleSchema),
        }} />

        <article className="w-full px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <div className="max-w-3xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs bg-primary-100 text-primary-600 px-3 py-1 rounded-full whitespace-nowrap">
                  {article.category}
                </span>
                {article.isHot && (
                  <span className="text-xs bg-accent-100 text-accent-700 px-3 py-1 rounded-full whitespace-nowrap">
                    热门
                  </span>
                )}
                {article.isNew && (
                  <span className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full whitespace-nowrap">
                    最新
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-xs text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(article.views)} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>
                  {formatViews(article.commentCount)} 评论
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-2 py-0.5 rounded whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose prose-sm max-w-none text-foreground-800 leading-relaxed mb-10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center justify-between py-6 border-t border-background-200">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                  <i className="ri-thumb-up-line"></i>
                  点赞
                </button>
                <button className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
                <button className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                  <i className="ri-bookmark-line"></i>
                  收藏
                </button>
              </div>
            </div>

            <div className="py-6 border-t border-background-200">
              <p className="text-xs text-foreground-500">
                声明：本站内容均来自网络公开信息整理，仅供娱乐参考，不构成任何建议。如有侵权请通过关于我们页面联系删除。
              </p>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-8 bg-background-100">
            <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">相关推荐</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </section>
        )}

        {moreArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-8">
            <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">猜你喜欢</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {moreArticles.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}